import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import getPath from 'lodash/get';
import isFunction from 'lodash/isFunction';
import head from 'lodash/head';
import Axios from 'axios';
import ModalDialog, {
  SaveButton,
  CloseButton,
  OtherButton,
} from '../../../../components/ModalDialog';
import Loading from '../../../../components/Loading';
import { ActionButton, InputText } from '../../../../styles/styled';
import Colors from '../../../../styles/colors';
import { emailValidator } from '../../../../helpers/validators';
import { getUserSessionIdtoken } from '../../../../helpers/userSession';
import { userManager } from '../../../../helpers/managers';
import { hasUserExceededAdminLimit } from '../../../../helpers/userinfoHelper';
import List from '../../../../components/List';
import LimitWarning from '../../../../components/LimitWarning';
import { ALERTS } from '../../../../helpers/texts';
import { publisherManager } from  '../../../../helpers/managers';

class Admin extends Component {
  constructor(props) {
    super(props);

    this.state = {
      publisher: {},
      ownerUser: {},
      monitoringUser: {},
      admins: [],
      selectedAdmins: [],
      show: false,
      inviting: false,
      removing: false,
      loading: true,
      newadmin: {
        name: '',
        email: '',
      },
      status: {
        success: false,
        message: '',
      },
    };
  }

  componentDidMount() {
    this.loadPublisherAdmins();
  }

  async loadPublisherAdmins() {
    try {
      const { publisherId } = this.props;

      const publisher = await publisherManager.getById(publisherId);
      if (isEmpty(publisher)) {
        return false;
      }

      const { administrators = {}, owner = '' } = publisher;
      const administratorIds = Object.entries(administrators)
        .filter(([, value]) => value !== false && value !== null)
        .map(([key]) => key);

      // pull every admins user data
      const records = await Promise.all(
        administratorIds.map(userId => {
          return new Promise(async resolve => {
            try {
              const email = await userManager.getProp(userId, 'email', []);
              if (isEmpty(email)) {
                throw new Error('Admin data does not exist');
              }

              // only load the needed data
              const { firstName, lastName } = await userManager.getProps(userId, [
                ['firstName', ''],
                ['lastName', ''],
              ]);

              // have to put `id` prop here
              // some users don't really have it, specially
              // if they registered first via mobile and then
              // invited as admin for example
              resolve({
                id: userId,
                email,
                firstName,
                lastName,
              });
            } catch (err) {
              resolve({});
            }
          });
        })
      );

      // only return the basic info
      // and make sure the user info is real
      const admins = records
        .filter(this.isAllUserDetailsValid)
        .map(({ id, email, firstName, lastName }) => {
          return {
            id,
            email: head(email),
            firstName,
            lastName,
            isOwner: isEqual(owner, id),
          };
        });

      // get any invalid admins and remove them
      this.handleRemovingOfInvalidAdmins(records);

      const ownerUser = admins.find(this.isOwner);
      const monitoringUser = admins.find(this.isMonitoring);

      // exclude monitoring account in the list
      const nadmins = admins.filter(admin => {
        return !this.isOwner(admin) && !this.isMonitoring(admin);
      });

      this.setState({
        publisher,
        ownerUser,
        monitoringUser,
        admins: nadmins,
        loading: false,
      });
    } catch (err) {
      console.log('There was a problem loading publisher admins', err.message);
    }
  }

  onAdminInputStateChange = statekey => {
    return e => {
      const value = e.target.value;
      const { newadmin } = this.state;
      this.setState(
        {
          newadmin: {
            ...newadmin,
            [statekey]: value,
          },
        },
        () => {
          this.setStatus();
        }
      );
    };
  };

  onAdminInputKeyPress = ({ key }) => key === 'Enter' && this.inviteNewAdmin();

  handleListStateChange = selectedAdmins => {
    this.setState({ selectedAdmins });
  };

  handleRemovingOfInvalidAdmins = async records => {
    const invalidAdmins = records.filter(admin => {
      return !this.isAllUserDetailsValid(admin);
    });

    if (!isEmpty(invalidAdmins)) {
      invalidAdmins.forEach(({ id: userId = '' }) => {
        this.removeUserAsAdmin(userId);
      });
    }
  };

  isAllUserDetailsValid = (user = {}) => {
    if (isEmpty(user)) {
      return false;
    }

    const { id, email } = user;
    return [id, email].every(value => !isEmpty(value));
  };

  isNewAdminDetailsValid = () => {
    const {
      newadmin: { name, email },
    } = this.state;

    return [!isEmpty(name), emailValidator(email)].every(value => !!value);
  };

  canEdit = () => {
    const { admins } = this.state;
    const { editMode } = this.props;
    return editMode && !isEmpty(admins);
  };

  toggleModal = () => {
    this.resetNewAdmin();
    this.setStatus();
    this.setState(({ show }) => ({
      show: !show,
    }));
  };

  isSelectedAdminsEmpty = () => {
    const { selectedAdmins } = this.state;
    return isEmpty(selectedAdmins);
  };

  isOwner = ({ isOwner }) => isOwner;

  isMonitoring = ({ email }) => {
    const { publisherId } = this.props;
    return isEqual(email, `${publisherId}@mysoundwise.com`);
  };

  setStatus = (success = false, message = '') => {
    this.setState({
      status: {
        success,
        message,
      },
    });
  };

  inviting = (value = true) => {
    return new Promise(resolve => {
      this.setState({ inviting: value }, resolve);
    });
  };

  removing = (value = true) => {
    return new Promise(resolve => {
      this.setState({ removing: value }, resolve);
    });
  };

  resetNewAdmin = () => {
    this.setState({
      newadmin: {
        name: '',
        email: '',
      },
    });
  };

  removeUserAsAdmin = async adminId => {
    const token = await getUserSessionIdtoken(true);
    return Axios.delete(`/api/publishers/administrator/${adminId}`, {
      headers: { token },
    });
  };

  removeAdmins = async () => {
    const { admins, selectedAdmins } = this.state;
    if (isEmpty(selectedAdmins)) {
      return false;
    }

    await this.removing();

    // multi update (same from common_publisher)
    const removedAdmins = [];
    for (const admin of selectedAdmins) {
      const { key: userId } = admin;
      this.removeUserAsAdmin(userId);

      // remove the admin from the list
      removedAdmins.push(userId);
    }

    const nadmins = admins.filter(admin => {
      return !removedAdmins.includes(admin.id);
    });

    await this.removing(false);
    this.setStatus(true, 'Selected Admins has been removed successfully.');
    this.setState({
      admins: nadmins,
      selectedAdmins: [],
    });
  };

  inviteNewAdmin = async () => {
    const {
      newadmin: { name, email },
    } = this.state;

    try {
      await this.inviting();
      const token = await getUserSessionIdtoken(true);
      const { data } = await Axios.post(
        `/api/publishers/administrator/`,
        { email, name },
        { headers: { token } }
      );
      await this.inviting(false);

      // determine the response
      const { success = false, completed = false, message = 'Unknown error' } = data || {};
      if (!success) {
        throw new Error(message);
      }

      this.setStatus(true, completed
        ? `User ${name} (${email}) has been assigned as an administrator`
        : `An email invitation has been sent to ${name} (${email})`
      );
      this.resetNewAdmin();
    } catch (err) {
      this.setStatus(false, `Unable to send an invitation to ${email}. ${err.message}`);
    }
  };

  renderStatus() {
    const {
      status: { success = false, message = '' },
    } = this.state;

    if (!success && isEmpty(message)) {
      return null;
    }

    const alertClass = classNames('alert text-medium', {
      'alert-success': success,
      'alert-danger': !success,
    });

    return (
      <div className={alertClass} role="alert">
        {message}
      </div>
    );
  }

  renderAdminLimitsWarning() {
    return (
      <LimitWarning
        message={ALERTS().ALLOWED_ADMINS.INVITE}
        onClickUpgrade={this.props.onClickUpgrade}
      />
    );
  }

  renderSpecialAdmins() {
    const { ownerUser = {}, monitoringUser = {} } = this.state;

    const getEmailtag = admin => {
      // do not show the email of monitoring account
      if (this.isMonitoring(admin)) {
        return null;
      }

      return <span className="email-tag text-medium">{`(${admin.email})`}</span>;
    };

    const getLabel = admin => {
      const label = this.isOwner(admin) ? 'Owner' : 'Monitoring';
      const labelClass = classNames('list-item label margin-two-right text-white', {
        'label-primary': this.isOwner(admin),
        'label-info': this.isMonitoring(admin),
      });

      return <div className={labelClass}>{label}</div>;
    };

    return [ownerUser, monitoringUser].map((user, index) => {
      if (isEmpty(user)) {
        return null;
      }

      const { firstName, lastName } = user;
      const name = `${firstName} ${lastName}`;
      return (
        <Unlisted key={index}>
          <div className="list-item">
            {name} {getEmailtag(user)}
          </div>
          {getLabel(user)}
        </Unlisted>
      );
    });
  }

  renderMonitoring() {
    const { monitoringUser } = this.state;
    if (isEmpty(monitoringUser)) {
      return null;
    }
  }

  renderAddAdmin() {
    const {
      newadmin: { name, email },
    } = this.state;

    return (
      <>
        <FlexiHeader className="text-large font-weight-700">
          <i className="material-icons margin-one-right">person_add</i>
          Invite new Admin
        </FlexiHeader>
        <NewAdminRow>
          <div className="row">
            <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">
              <InputText
                type="text"
                placeholder="Name"
                onChange={this.onAdminInputStateChange('name')}
                value={name}
              />
            </div>
            <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">
              <InputText
                type="email"
                placeholder="Email"
                onChange={this.onAdminInputStateChange('email')}
                onKeyPress={this.onAdminInputKeyPress}
                value={email}
              />
            </div>
            <div className="col-lg-4 col-md-4 col-sm-4 col-xs-12">{this.renderInviteButton()}</div>
          </div>
        </NewAdminRow>
      </>
    );
  }

  renderBody() {
    const { admins, loading } = this.state;
    const { editMode } = this.props;
    if (loading) {
      return <Loading text="Loading admins..." />;
    }

    const items = admins.map(admin => {
      const { id, email, firstName, lastName } = admin;
      return {
        key: id,
        values: [`${firstName} ${lastName}`, email],
      };
    });

    const limited = hasUserExceededAdminLimit();
    const adminWrapperClass = classNames({ 'no-margin': limited });
    return (
      <>
        {limited && this.renderAdminLimitsWarning()}
        <SpecialAdmins>{this.renderSpecialAdmins()}</SpecialAdmins>
        {!isEmpty(admins) && (
          <>
            <FlexiHeader className="text-large font-weight-700">
              <i className="material-icons margin-one-right">people</i>
              Active Admins
            </FlexiHeader>
            <AdminWrapper className={adminWrapperClass}>
              <List
                headers={['Name', this.renderDeleteButton()]}
                items={items}
                editMode={editMode}
                onStateChange={this.handleListStateChange}
              />
            </AdminWrapper>
          </>
        )}
        {this.renderStatus()}
        {!limited && this.renderAddAdmin()}
      </>
    );
  }

  renderDeleteButton() {
    const { removing } = this.state;

    if (!this.canEdit() || this.isSelectedAdminsEmpty()) {
      return null;
    }

    if (removing) {
      return (
        <Loading
          text="Removing"
          type="circle"
          align="left"
          size={20}
          fontSize={1.5}
          color="#f44336"
          fill="#f44336"
        />
      );
    }

    return (
      <DeleteButton
        className="btn"
        disabled={this.isSelectedAdminsEmpty()}
        onClick={this.removeAdmins}>
        <div className="icon">
          <i className="material-icons no-margin" title="Delete">
            delete
          </i>
        </div>
      </DeleteButton>
    );
  }

  renderInviteButton() {
    const { inviting } = this.state;

    const content = inviting ? (
      <Loading
        text="Inviting"
        type="circle"
        align="center"
        size={19}
        fontSize={1.4}
        color="#fff"
        fill="#fff"
        textStyle={{ margin: '0 5px' }}
      />
    ) : (
      'Invite'
    );

    return (
      <InviteButton
        className="btn"
        disabled={inviting || !this.isNewAdminDetailsValid()}
        onClick={this.inviteNewAdmin}>
        {content}
      </InviteButton>
    );
  }

  render() {
    const { show, inviting, removing } = this.state;
    const { renderStartButton } = this.props;
    const setupButtom = isFunction(renderStartButton) ? (
      renderStartButton(this.toggleModal)
    ) : (
      <SetupButton onClick={this.toggleModal}>Invite Another Admin</SetupButton>
    );

    return (
      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">{setupButtom}</div>
        <ModalDialog
          title="Manage Admins"
          open={show}
          actions={[
            <CloseButton
              className="btn text-medium naked"
              onClick={this.toggleModal}
              disabled={inviting || removing}>
              Close
            </CloseButton>,
          ]}
          contentStyle={{
            width: 780,
            maxWidth: 'none',
            transform: 'translate(0px, 15px)',
          }}
          bodyStyle={{ overflowY: 'auto' }}
          actionsContainerStyle={{
            padding: '20px 24px',
            border: '1px solid #f6f6f6',
          }}>
          <div className="row">
            <div className="col-md-12">{this.renderBody()}</div>
          </div>
        </ModalDialog>
      </div>
    );
  }
}

const SetupButton = styled(ActionButton)`
  background-color: ${Colors.mainOrange};
  border-radius: 3px;
`;

const NewAdminRow = styled.div`
  padding: 15px;
  background: #fffbf9;
  border: 2px dashed #fff1eb;
`;

const FlexiHeader = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const AdminWrapper = styled.div`
  margin-bottom: 20px;
`;

const SpecialAdmins = styled(AdminWrapper)`
  background: #fffbf9;
  padding: 5px 0;
  border-radius: 5px;
  border: 2px dashed #fff1eb;
`;

const Unlisted = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 5px;

  .list-item {
    color: ${Colors.fontBlack};
    padding: 5px 10px;

    .email-tag {
      color: #8a8a8a;
      margin-left: 5px;
    }
  }
`;

const InviteButton = styled(SaveButton)`
  width: 100%;
  text-transform: initial;
  padding: 9px 30px;
`;

const DeleteButton = styled(OtherButton)`
  padding: 3px 5px;
  background-color: #f44336;
  border: 1px solid #f44336;

  .icon {
    display: flex;
    align-items: center;

    i {
      font-size: 2rem;
    }
  }

  &:hover {
    .icon {
      color: #fff;
    }
  }
`;

Admin.propTypes = {
  publisherId: PropTypes.string,
  editMode: PropTypes.bool,
  renderStartButton: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
};

Admin.defaultProps = {
  publisherId: '',
  editMode: false,
  renderStartButton: false,
};

export default Admin;
